import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { toast } from 'react-toastify';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import { activateLicense as apiActivateLicense, getCredits, updateDevice } from '@/api/vendo';
import { hasSpendableCredit } from '@/utils/creditGates';
import type { VendoDevice } from '@/pages/Admin/Vendo';

interface Props {
   data: {
      device: VendoDevice;
      onBrowsePartners: () => void;
   };
}

const PARTNER_SAVINGS = 100;

const ActivateLicense = ({ data }: Props) => {
   const { handleClose } = useUIStore();
   const { device, onBrowsePartners } = data;
   const queryClient = useQueryClient();
   const refetchDevices = useVendoDevicesStore((s) => s.fetch);
   const { data: creditsData } = useQuery({
      queryKey: ['credits'],
      queryFn: getCredits,
   });
   const credits = creditsData?.credits ?? 0;
   const [isLoading, setIsLoading] = useState(false);

   // Admins are never blocked by an empty wallet — the server issues the one
   // credit the activation needs, then charges it as normal, so the activation
   // still shows up in the usage figures. Only the button gate changes here.
   const unlimited = creditsData?.unlimited ?? false;
   const hasCredits = hasSpendableCredit(credits, unlimited);
   const isRenew = device.licenseStatus === 'expired';
   const [autoRenew, setAutoRenew] = useState<boolean>(device.autoRenewLicense);
   const [autoRenewSaving, setAutoRenewSaving] = useState(false);

   const handleAutoRenewToggle = async (next: boolean) => {
      // Optimistic flip — the toggle should feel instant. Roll back on error.
      const prev = autoRenew;
      setAutoRenew(next);
      setAutoRenewSaving(true);
      try {
         await updateDevice(device._id, { autoRenewLicense: next });
         refetchDevices();
      } catch (err: any) {
         setAutoRenew(prev);
         toast.error(err?.response?.data?.error || 'Could not save auto-renew');
      } finally {
         setAutoRenewSaving(false);
      }
   };

   const handleRenew = async () => {
      if (isLoading || !hasCredits) return;
      setIsLoading(true);
      try {
         await apiActivateLicense(device._id);
         queryClient.invalidateQueries({ queryKey: ['credits'] });
         refetchDevices();
         toast.success(
            `${isRenew ? 'Renewed' : 'Activated'} ${device.name} · 1 credit used`,
            { autoClose: 2500 }
         );
         handleClose();
      } catch (err: any) {
         const status = err?.response?.status;
         const msg =
            status === 402
               ? 'Insufficient credits — buy more from a partner.'
               : err?.response?.data?.error || 'Activation failed';
         toast.error(msg);
      } finally {
         setIsLoading(false);
      }
   };

   const handleBuy = () => {
      handleClose();
      onBrowsePartners();
   };

   return (
      <Wrapper>
         <Summary>
            <DeviceTitle>{device.name}</DeviceTitle>
            <DeviceMeta>
               <MetaId>{device.id}</MetaId>
               <StatusPill $expired={device.licenseStatus === 'expired'}>
                  {device.licenseStatus === 'expired'
                     ? 'Expired'
                     : device.licenseStatus === 'trial'
                     ? `Trial · ${device.trialDaysLeft ?? 0}d left`
                     : 'Not activated'}
               </StatusPill>
            </DeviceMeta>
         </Summary>

         <CreditsBanner $hasCredits={hasCredits}>
            <CreditsIcon>🎟️</CreditsIcon>
            <CreditsBody>
               <CreditsLabel>
                  You have <strong>{credits}</strong> credit{credits === 1 ? '' : 's'}
               </CreditsLabel>
               <CreditsSub>
                  {unlimited && credits < 1
                     ? 'Admin account — the credit is issued for you, and still counted as used'
                     : hasCredits
                       ? '1 credit = a lifetime license for this device'
                       : "You'll need to buy credits to activate"}
               </CreditsSub>
            </CreditsBody>
         </CreditsBanner>

         <AutoRenewRow>
            <AutoRenewCheckbox
               type="checkbox"
               id="ar-toggle"
               checked={autoRenew}
               disabled={autoRenewSaving}
               onChange={(e) => handleAutoRenewToggle(e.target.checked)}
            />
            <AutoRenewLabel htmlFor="ar-toggle">
               <AutoRenewTitle>
                  Auto-activate when the trial ends {autoRenewSaving && '· saving…'}
               </AutoRenewTitle>
               <AutoRenewDesc>
                  {autoRenew
                     ? 'When the trial ends, we use 1 credit automatically for a lifetime license — only if the device is online.'
                     : 'When the trial ends the portal locks until you activate. You will get an email.'}
               </AutoRenewDesc>
            </AutoRenewLabel>
         </AutoRenewRow>

         {hasCredits ? (
            <>
               <PrimaryCard>
                  <PrimaryIcon>{isRenew ? '🔁' : '✅'}</PrimaryIcon>
                  <PrimaryBody>
                     <PrimaryTitle>
                        {isRenew ? 'Renew License' : 'Activate License'}
                     </PrimaryTitle>
                     <PrimaryDesc>
                        {unlimited && credits < 1 ? (
                           <>
                              Issues <strong>1 credit</strong> to you and spends it ·
                              lifetime license, applied instantly
                           </>
                        ) : (
                           <>
                              Uses <strong>1 credit</strong> from your balance · lifetime
                              license, applied instantly — no renewals
                           </>
                        )}
                     </PrimaryDesc>
                  </PrimaryBody>
                  <PrimaryCost>
                     <CostNum>−1</CostNum>
                     <CostUnit>credit</CostUnit>
                  </PrimaryCost>
               </PrimaryCard>

               <SecondaryLinkRow>
                  <SecondaryLink onClick={handleBuy}>
                     Low on credits? Buy more →
                  </SecondaryLink>
               </SecondaryLinkRow>

               <BtnRow>
                  <div onClick={handleClose}>
                     <Btn title="Cancel" type="outline" size="small" />
                  </div>
                  <div onClick={handleRenew}>
                     <Btn
                        title={
                           isLoading
                              ? isRenew
                                 ? 'Renewing...'
                                 : 'Activating...'
                              : isRenew
                              ? 'Renew · Use 1 credit'
                              : 'Activate · Use 1 credit'
                        }
                        type={isLoading ? 'disabled' : 'normal'}
                        size="small"
                        bgcolor={isLoading ? colors.GRAY10 : colors.PRIMARY}
                        textcolor={isLoading ? colors.GRAY1 : colors.WHITE}
                     />
                  </div>
               </BtnRow>
            </>
         ) : (
            <>
               <PrimaryCard onClick={handleBuy} $clickable>
                  <PrimaryIcon>💳</PrimaryIcon>
                  <PrimaryBody>
                     <PrimaryTitleRow>
                        <PrimaryTitle>Buy License Credits</PrimaryTitle>
                        <SavingsBadge>save ₱{PARTNER_SAVINGS}/yr via partners</SavingsBadge>
                     </PrimaryTitleRow>
                     <PrimaryDesc>
                        Opens the Partners page — pick a local reseller or buy direct from
                        SmartFi (₱500/credit). After buying, come back here to activate.
                     </PrimaryDesc>
                  </PrimaryBody>
                  <PrimaryArrow>→</PrimaryArrow>
               </PrimaryCard>

               <BtnRow>
                  <div onClick={handleClose}>
                     <Btn title="Cancel" type="outline" size="small" />
                  </div>
               </BtnRow>
            </>
         )}
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 520px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 12px;
   @media (max-width: 768px) { width: 100%; }
`;

const Summary = styled.div`
   padding: 10px 12px;
   border-radius: 8px;
   background: var(--app-brand-tint);
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const DeviceTitle = styled.span`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const DeviceMeta = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;

const MetaId = styled.span`
   font-family: monospace;
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const StatusPill = styled.span<{ $expired: boolean }>`
   display: inline-block;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 10px;
   font-weight: 700;
   background: ${({ $expired }) => ($expired ? `${colors.DANGER}18` : `${colors.WARNING}18`)};
   color: ${({ $expired }) => ($expired ? colors.DANGER : colors.WARNING)};
`;

/* Credits banner */

const CreditsBanner = styled.div<{ $hasCredits: boolean }>`
   display: flex;
   gap: 12px;
   align-items: center;
   padding: 10px 14px;
   background: ${({ $hasCredits }) =>
      $hasCredits ? `${colors.SUCCESS}10` : `${colors.WARNING}10`};
   border: 1px solid
      ${({ $hasCredits }) =>
         $hasCredits ? `${colors.SUCCESS}30` : `${colors.WARNING}30`};
   border-radius: 10px;
`;

const CreditsIcon = styled.span`
   font-size: 22px;
   flex-shrink: 0;
`;

const CreditsBody = styled.div`
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

const CreditsLabel = styled.p`
   font-size: 13px;
   color: ${colors.MAIN_DARK};
`;

const CreditsSub = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

/* Primary action card (Renew OR Buy) */

const PrimaryCard = styled.div<{ $clickable?: boolean }>`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 14px 16px;
   border: 2px solid ${colors.PRIMARY};
   background: ${colors.PRIMARY}08;
   border-radius: 12px;
   cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
   transition: all 0.15s;
   ${({ $clickable }) =>
      $clickable &&
      `&:hover { background: ${colors.PRIMARY}12; }`}
`;

const PrimaryIcon = styled.span`
   font-size: 26px;
   flex-shrink: 0;
`;

const PrimaryBody = styled.div`
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 4px;
   min-width: 0;
`;

const PrimaryTitleRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

const PrimaryTitle = styled.span`
   font-size: 14px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;

const PrimaryDesc = styled.span`
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;

const PrimaryCost = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 0;
   flex-shrink: 0;
`;

const CostNum = styled.span`
   font-size: 22px;
   font-weight: 800;
   color: ${colors.PRIMARY};
   line-height: 1;
`;

const CostUnit = styled.span`
   font-size: 10px;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const PrimaryArrow = styled.span`
   font-size: 22px;
   color: ${colors.PRIMARY};
   flex-shrink: 0;
`;

const SavingsBadge = styled.span`
   font-size: 10px;
   font-weight: 700;
   background: ${colors.SUCCESS}18;
   color: ${colors.SUCCESS};
   padding: 1px 7px;
   border-radius: 10px;
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const SecondaryLinkRow = styled.div`
   text-align: center;
`;

const SecondaryLink = styled.span`
   font-size: 12px;
   color: ${colors.PRIMARY};
   cursor: pointer;
   font-weight: 600;
   &:hover { text-decoration: underline; }
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 4px;
`;

const AutoRenewRow = styled.div`
   display: flex;
   gap: 10px;
   padding: 10px 12px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;

const AutoRenewCheckbox = styled.input`
   width: 16px;
   height: 16px;
   margin-top: 2px;
   accent-color: ${colors.PRIMARY};
   cursor: pointer;
`;

const AutoRenewLabel = styled.label`
   display: flex;
   flex-direction: column;
   gap: 2px;
   cursor: pointer;
   flex: 1;
`;

const AutoRenewTitle = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const AutoRenewDesc = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

export default ActivateLicense;
